import { useMutation, useQuery } from '@tanstack/react-query'
import type { DictationReviewSnapshot, DictationTone } from '@viapeople/components'
import {
	Button,
	ChoiceSelectField,
	cn,
	Forms9BoxRating,
	FormsAggregation,
	FormsAverage,
	FormsCheckbox,
	FormsDataViewer,
	FormsDatePicker,
	FormsDivider,
	FormsExternalLink,
	FormsHeader,
	FormsImage,
	FormsInstructions,
	FormsNumberField,
	FormsParagraph,
	FormsRating,
	FormsReviewViewer,
	FormsSelectField,
	FormsTextArea,
	FormsTextField,
	getColumns,
	groupChildrenByColumn,
	IaIcon,
	normalizeMax,
	Text,
	Tooltip,
	TooltipContent,
	TooltipTrigger,
} from '@viapeople/components'
import type {
	DataViewerCustomProps,
	FormsReviewViewerCustomProps,
	LabelSize,
} from '@viapeople/components/src/components/forms/types'
import { getLabelVariant } from '@viapeople/components/src/components/forms/utils'
import { useMemo, useState } from 'react'
import { type Control, Controller, useWatch } from 'react-hook-form'
import { v7 as uuidv7 } from 'uuid'
import { useFeatureFlag } from '../../../hooks/useFeatureFlag'
import { useToast } from '../../../utils/ToastContext'
import { trpc } from '../../../utils/trpc'
import {
	type FeedbackSuggestion,
	FeedbackSuggestionsLoading,
	FeedbackSuggestionsPanel,
} from '../../feedback/FeedbackSuggestionsPanel'
import { useImproveFeedback } from '../../feedback/useImproveFeedback'
import { type AiSummary, AiSummaryList } from './AiSummaryList'
import { FormsExternalDataView } from './FormsExternalDataView'
import { HrReviewFlagComponent } from './HrReviewFlagComponent'
import {
	CheckboxReports,
	DatePickerReports,
	type FormComponentWithReports,
	NumberFieldReports,
	RadioGroupReports,
	RatingReports,
	type Report,
	SelectFieldReports,
	TextAreaReports,
	TextFieldReports,
} from './reports'
import type { ProcessedFormComponent } from './types'
import { useReviewViewerUrl, useReviewViewerUrls } from './useReviewViewerUrl'
import { getInitialBoolean, getInitialFormValue } from './valueUtils'

type FormValues = Record<string, string | number | boolean>

const ContainerComponent = ({
	component,
	control,
	evaluationId,
	currentStepId,
	userRole,
	canEdit,
	allowCAEdit,
	taskId,
	refetch,
	fieldErrors,
	allFormComponents,
	aiSummaries,
	onDictationSnapshot,
	onSubmitDraft,
	isHidden,
	evaluateeExternalId,
}: {
	component: FormComponentWithReports
	control: Control<FormValues>
	evaluationId: string
	currentStepId: string
	userRole: string
	canEdit: boolean
	allowCAEdit?: boolean
	taskId?: string
	refetch?: () => void
	fieldErrors?: Set<string>
	allFormComponents?: FormComponentWithReports[]
	aiSummaries?: AiSummary[]
	onDictationSnapshot?: (snapshot: DictationReviewSnapshot) => void
	onSubmitDraft?: () => void
	isHidden?: (componentId: string) => boolean
	evaluateeExternalId?: string
}) => {
	const props = component.custom as {
		label: string
		labelColor: string
		labelSize?: LabelSize
		labelWeight?: 'regular' | 'semibold' | 'hidden'
		description: string
		descriptionColor: string
		isSection: boolean
		showResponseAverage?: boolean
		backgroundColor?: string
	}

	const columns = getColumns(component.custom as Record<string, unknown>)

	const numericFields = useMemo(() => {
		const fields: {
			id: string
			label: string
			type: 'rating' | 'select' | 'number'
			reportOnly?: boolean
			reports?: Report[]
		}[] = []

		const collectFields = (comp: FormComponentWithReports) => {
			if (comp.name === 'rating' || comp.name === 'select' || comp.name === 'number') {
				fields.push({
					id: comp.id,
					label: comp.label || comp.name,
					type: comp.name as 'rating' | 'select' | 'number',
					reportOnly: comp.reportOnly,
					reports: comp.reports,
				})
			}
			if (comp.children) {
				comp.children.forEach(collectFields)
			}
		}

		if (component.children) {
			component.children.forEach(collectFields)
		}

		return fields
	}, [component.children])

	const watchedValues = useWatch({
		control,
		name: numericFields.map((f) => f.id),
	})

	const average = useMemo(() => {
		if (numericFields.length === 0) return null

		const values: number[] = []

		numericFields.forEach((field, index) => {
			// First, try to get report values (for read-only report components)
			// Calculate from individual submissions to handle cases where some are empty
			if (field.reports && field.reports.length > 0) {
				field.reports.forEach((report: Report) => {
					if (report.submissions && Array.isArray(report.submissions)) {
						for (const submission of report.submissions) {
							if (submission.isPlaceholder) {
								continue
							}

							if (submission && submission.value !== undefined && submission.value !== null) {
								const numericValue = Number.parseFloat(String(submission.value))
								if (!Number.isNaN(numericValue) && Number.isFinite(numericValue)) {
									values.push(numericValue)
								}
							}
						}
					}
				})
			}
			// If no reports, try to get the live form value (for editable input components)
			else {
				const watchedValue = watchedValues?.[index]
				let value: string | undefined

				if (watchedValue !== undefined) {
					value = String(watchedValue)
				} else {
					const findComponent = (comps?: FormComponentWithReports[]): FormComponentWithReports | undefined => {
						if (!comps) return undefined
						for (const comp of comps) {
							if (comp.id === field.id) return comp
							const found = findComponent(comp.children)
							if (found) return found
						}
						return undefined
					}
					const comp = findComponent(component.children)
					value = comp?.value as string
				}

				if (value && value !== '') {
					const numericValue = Number.parseFloat(value)
					if (!Number.isNaN(numericValue) && Number.isFinite(numericValue)) {
						values.push(numericValue)
					}
				}
			}
		})

		if (values.length === 0) return null

		const sum = values.reduce((acc, val) => acc + val, 0)
		return (sum / values.length).toFixed(2)
	}, [watchedValues, numericFields, component.children])

	if (isHidden?.(component.id)) return null

	return (
		<div
			id={component.id}
			className={cn(
				'component-section flex flex-col pl-6 gap-4',
				props.isSection && 'border rounded-lg border-neutral-200 py-4',
				props.backgroundColor && 'rounded-md',
			)}
			style={{ backgroundColor: props.backgroundColor || undefined }}>
			<Text
				variant={getLabelVariant(props.labelSize, 'h6')}
				weight={(props.labelWeight !== 'hidden' && props.labelWeight) || null}
				className={cn(`text-[${props.labelColor}]`, props.labelWeight === 'hidden' && 'hidden')}>
				{props.label}
			</Text>
			{props.description && (
				<Text variant='small' weight='regular' className={cn(`text-[${props.descriptionColor}]`)}>
					{props.description}
				</Text>
			)}
			{columns > 1 ? (
				<div
					className='component-section-content grid gap-4 px-6 pt-5'
					style={{ gridTemplateColumns: `repeat(${columns}, 1fr)` }}>
					{groupChildrenByColumn(component.children || [], columns).map((colChildren, colIdx) => (
						// biome-ignore lint/suspicious/noArrayIndexKey: column index is stable for grid layout
						<div key={colIdx} className='flex flex-col gap-2'>
							{colChildren.map((child) => {
								const summariesAfterChild =
									aiSummaries?.filter((summary) => {
										const displayAfter = summary.config?.displayAfterComponentId
										return displayAfter && displayAfter === child.id
									}) || []
								return (
									<div key={child.id}>
										<ComponentRenderer
											component={child}
											control={control}
											userRole={userRole}
											evaluationId={evaluationId}
											currentStepId={currentStepId}
											canEdit={canEdit}
											allowCAEdit={allowCAEdit}
											taskId={taskId}
											refetch={refetch}
											fieldErrors={fieldErrors}
											allFormComponents={allFormComponents}
											aiSummaries={aiSummaries}
											onDictationSnapshot={onDictationSnapshot}
											onSubmitDraft={onSubmitDraft}
											isHidden={isHidden}
											evaluateeExternalId={evaluateeExternalId}
										/>
										{summariesAfterChild.length > 0 && (
											<div className='my-6'>
												<AiSummaryList aiSummaries={summariesAfterChild} evaluationId={evaluationId} />
											</div>
										)}
									</div>
								)
							})}
						</div>
					))}
				</div>
			) : (
				<div className='component-section-content flex flex-col px-6 gap-2 pt-5'>
					{component.children?.map((child) => {
						const summariesAfterChild =
							aiSummaries?.filter((summary) => {
								const displayAfter = summary.config?.displayAfterComponentId
								return displayAfter && displayAfter === child.id
							}) || []
						return (
							<div key={child.id}>
								<ComponentRenderer
									component={child}
									control={control}
									userRole={userRole}
									evaluationId={evaluationId}
									currentStepId={currentStepId}
									canEdit={canEdit}
									allowCAEdit={allowCAEdit}
									taskId={taskId}
									refetch={refetch}
									fieldErrors={fieldErrors}
									allFormComponents={allFormComponents}
									aiSummaries={aiSummaries}
									onDictationSnapshot={onDictationSnapshot}
									onSubmitDraft={onSubmitDraft}
									isHidden={isHidden}
									evaluateeExternalId={evaluateeExternalId}
								/>
								{summariesAfterChild.length > 0 && (
									<div className='my-6'>
										<AiSummaryList aiSummaries={summariesAfterChild} evaluationId={evaluationId} />
									</div>
								)}
							</div>
						)
					})}
				</div>
			)}

			{!props.isSection &&
				average !== null &&
				numericFields.length > 0 &&
				props.label &&
				props.showResponseAverage !== false && (
					<>
						<div className='w-full h-px bg-neutral-200 mx-6' style={{ width: 'calc(100% - 3rem)' }} />
						<div className='px-6 pb-2'>
							<Text variant='small' className='text-neutral-600'>
								<span className='font-medium'>Average {props.label}:</span> {average}
							</Text>
						</div>
					</>
				)}
		</div>
	)
}

/**
 * Recursively clones a component tree as a template (without evaluationId),
 * generating new UUIDs and updating containerId references.
 * Used for preserving template container structure in nested repeatables.
 */
const cloneComponentTreeAsTemplate = (
	component: FormComponentWithReports,
	newContainerId: string,
): FormComponentWithReports => {
	const newId = uuidv7()

	const clonedChildren = component.children?.map((child) => cloneComponentTreeAsTemplate(child, newId))

	return {
		...component,
		id: newId,
		containerId: newContainerId,
		evaluationId: null, // Keep as template
		value: undefined,
		children: clonedChildren,
	}
}

/**
 * Recursively clones a component tree with evaluationId set,
 * generating new UUIDs and updating containerId references.
 * For nested repeatables, preserves the template container structure.
 */
const cloneComponentWithEvaluation = (
	component: FormComponentWithReports,
	newContainerId: string,
	evaluationId: string,
): FormComponentWithReports => {
	const newId = uuidv7()

	// Clone children recursively with special handling for nested repeatables
	const clonedChildren = component.children?.map((child) => {
		// For nested repeatables, preserve template containers without evaluationId
		if (component.name === 'repeatable' && child.name === 'container' && !child.evaluationId) {
			return cloneComponentTreeAsTemplate(child, newId)
		}
		return cloneComponentWithEvaluation(child, newId, evaluationId)
	})

	return {
		...component,
		id: newId,
		containerId: newContainerId,
		evaluationId,
		value: undefined,
		children: clonedChildren,
	}
}

const RepeatableComponent = ({
	component,
	control,
	evaluationId,
	currentStepId,
	userRole,
	canEdit,
	allowCAEdit,
	taskId,
	refetch,
	fieldErrors,
	allFormComponents,
	aiSummaries,
	onDictationSnapshot,
	onSubmitDraft,
	isHidden,
	evaluateeExternalId,
}: {
	component: FormComponentWithReports
	control: Control<FormValues>
	evaluationId: string
	currentStepId: string
	userRole: string
	canEdit: boolean
	allowCAEdit?: boolean
	taskId?: string
	refetch?: () => void
	fieldErrors?: Set<string>
	allFormComponents?: FormComponentWithReports[]
	aiSummaries?: AiSummary[]
	onDictationSnapshot?: (snapshot: DictationReviewSnapshot) => void
	onSubmitDraft?: () => void
	isHidden?: (componentId: string) => boolean
	evaluateeExternalId?: string
}) => {
	const toast = useToast()
	const props = component.custom as {
		label: string
		labelColor: string
		labelSize?: LabelSize
		labelWeight?: 'regular' | 'semibold' | 'hidden'
		min?: number
		max?: number
		backgroundColor?: string
		buttonLabel?: string
		instanceLabel?: string
	}
	const columns = getColumns(component.custom as Record<string, unknown>)
	// max <= 0 is treated as unset (unlimited). A stored 0 breaks "N of max" and blocks the add button.
	const effectiveMax = normalizeMax(props.max)
	const addComponentsMutation = useMutation({
		...trpc.client.cycles.execution.addComponents.mutationOptions(),
	})
	const removeComponentsMutation = useMutation({
		...trpc.client.cycles.execution.removeComponents.mutationOptions(),
	})

	if (isHidden?.(component.id)) return null

	// Find template container (has no evaluationSubmissionId)
	const templateContainer = component.children?.find((child) => child.name === 'container' && !child.evaluationId)

	// Get instance containers for this evaluation (have evaluationSubmissionId === evaluationId)
	// Sort by createdAt to ensure newest instances appear at the end
	const persistedInstanceContainers =
		component.children
			?.filter((child) => child.name === 'container' && child.evaluationId === evaluationId)
			.sort((a, b) => {
				const aWithDates = a as FormComponentWithReports & { createdAt?: string }
				const bWithDates = b as FormComponentWithReports & { createdAt?: string }
				const dateA = aWithDates.createdAt ? new Date(aWithDates.createdAt).getTime() : 0
				const dateB = bWithDates.createdAt ? new Date(bWithDates.createdAt).getTime() : 0
				return dateA - dateB
			}) || []

	// All containers to display (template + persisted instances + temp instances)
	const allInstanceContainers = [...(templateContainer ? [templateContainer] : []), ...persistedInstanceContainers]

	const currentInstanceCount = allInstanceContainers.length

	const canAddMore =
		!component.reportOnly && !!component.write && (effectiveMax === undefined || currentInstanceCount < effectiveMax)
	const canRemove = !component.reportOnly && !!component.write && currentInstanceCount > (props.min ?? 0)

	// Handler to add a new instance
	const handleAddInstance = () => {
		if (!canAddMore || !templateContainer) return
		const containerId = uuidv7()

		// Clone template container and its children recursively
		// This properly handles nested repeatables by preserving their template containers
		const newInstanceContainer: FormComponentWithReports = {
			...templateContainer,
			id: containerId,
			evaluationId,
			containerId: component.id,
			children: templateContainer.children?.map((template) =>
				cloneComponentWithEvaluation(template, containerId, evaluationId),
			),
		}

		addComponentsMutation.mutate([newInstanceContainer], {
			onSuccess: refetch,
			onError: (error) => {
				toast.showToast(error.message, 'error')
			},
		})
	}

	// Handler to remove an instance
	const handleRemoveInstance = (component: ProcessedFormComponent, index: number) => {
		if (!canRemove) return

		// Index 0 is always the template container, can't remove it
		if (index === 0) {
			toast.showToast('cannot remove template container', 'info')
			return
		}

		removeComponentsMutation.mutate([component], {
			onSuccess: refetch,
			onError: (error) => {
				toast.showToast(error.message, 'error')
			},
		})
	}

	return (
		<div
			id={component.id}
			className={cn(
				'flex flex-col gap-4 my-4 p-6 border rounded-lg border-neutral-200 bg-white',
				props.backgroundColor && 'rounded-md',
			)}
			style={{ backgroundColor: props.backgroundColor || undefined }}>
			<div className='flex items-center justify-between gap-2'>
				<div className='flex items-center gap-2 flex-1'>
					{props.label && (
						<Text
							variant={getLabelVariant(props.labelSize, 'h6')}
							weight={(props.labelWeight !== 'hidden' && props.labelWeight) || null}
							className={cn(props.labelWeight === 'hidden' && 'hidden')}
							style={{ color: props.labelColor }}>
							{props.label}
						</Text>
					)}
				</div>
				{effectiveMax !== undefined && (
					<span className='px-2 py-1 text-xs font-medium text-primary-700 bg-primary-100 rounded'>
						{currentInstanceCount} of {effectiveMax}
					</span>
				)}
			</div>

			{/* Render instance containers */}
			<div className='flex flex-col gap-4'>
				{currentInstanceCount === 0 && (
					<Text variant='small' className='text-neutral-500 italic'>
						No instances yet. Click "+ Add" to create one.
					</Text>
				)}

				{/* Render each instance container */}
				{allInstanceContainers.map((instanceContainer, instanceIndex) => {
					return (
						<div
							key={instanceContainer.id}
							className='flex flex-col gap-2 p-4 border border-neutral-200 rounded-md bg-white'>
							<div className='flex items-center justify-between mb-2'>
								<span className='px-2 py-1 text-xs font-medium text-primary-700 bg-primary-100 rounded'>
									{`${props.instanceLabel || 'Instance'} ${instanceIndex + 1}`}
								</span>
								{canEdit && canRemove && instanceIndex > 0 && (
									<button
										type='button'
										onClick={() => handleRemoveInstance(instanceContainer, instanceIndex)}
										disabled={!canRemove}
										className='px-2 py-1 text-xs font-medium rounded cursor-pointer text-error-700 bg-error-100 hover:bg-error-200'>
										Remove
									</button>
								)}
							</div>
							{columns > 1 ? (
								<div className='grid gap-4' style={{ gridTemplateColumns: `repeat(${columns}, 1fr)` }}>
									{groupChildrenByColumn(instanceContainer.children || [], columns).map((colChildren, colIdx) => (
										// biome-ignore lint/suspicious/noArrayIndexKey: columns are positional with no stable identifier
										<div key={colIdx} className='flex flex-col gap-2'>
											{colChildren.map((child) => (
												<ComponentRenderer
													key={child.id}
													component={child}
													control={control}
													userRole={userRole}
													evaluationId={evaluationId}
													currentStepId={currentStepId}
													canEdit={canEdit}
													allowCAEdit={allowCAEdit}
													taskId={taskId}
													refetch={refetch}
													fieldErrors={fieldErrors}
													allFormComponents={allFormComponents}
													aiSummaries={aiSummaries}
													onDictationSnapshot={onDictationSnapshot}
													onSubmitDraft={onSubmitDraft}
													isHidden={isHidden}
													evaluateeExternalId={evaluateeExternalId}
												/>
											))}
										</div>
									))}
								</div>
							) : (
								instanceContainer.children?.map((child) => (
									<ComponentRenderer
										key={child.id}
										component={child}
										control={control}
										userRole={userRole}
										evaluationId={evaluationId}
										currentStepId={currentStepId}
										canEdit={canEdit}
										allowCAEdit={allowCAEdit}
										taskId={taskId}
										refetch={refetch}
										fieldErrors={fieldErrors}
										allFormComponents={allFormComponents}
										aiSummaries={aiSummaries}
										onDictationSnapshot={onDictationSnapshot}
										onSubmitDraft={onSubmitDraft}
										isHidden={isHidden}
										evaluateeExternalId={evaluateeExternalId}
									/>
								))
							)}
						</div>
					)
				})}
			</div>
			{canEdit && canAddMore && (
				<div className='flex justify-end mt-2'>
					<button
						type='button'
						onClick={handleAddInstance}
						className={cn(
							'px-3 py-1 text-sm font-medium rounded hover:bg-primary-200 text-primary-700 bg-primary-100 cursor-pointer',
						)}>
						{props.buttonLabel || '+ Add'}
					</button>
				</div>
			)}
		</div>
	)
}

const findComponentById = (id: string, components?: FormComponentWithReports[]): FormComponentWithReports | null => {
	if (!components) return null
	for (const comp of components) {
		if (comp.id === id) return comp
		if (comp.children) {
			const found = findComponentById(id, comp.children)
			if (found) return found
		}
	}
	return null
}

export const AverageComponent = ({
	component,
	control,
	allFormComponents,
	userRole,
}: {
	component: FormComponentWithReports
	control: Control<FormValues>
	allFormComponents?: FormComponentWithReports[]
	userRole: string
}) => {
	const { selectedComponentIds, excludeParticipants, selfRatingsOnly } = component.custom as {
		selectedComponentIds: string[]
		excludeParticipants?: boolean
		selfRatingsOnly?: boolean
	}

	const watchedValues = useWatch({
		control,
		name: selectedComponentIds || [],
	})

	const averageValue = useMemo(() => {
		if (!selectedComponentIds || selectedComponentIds.length === 0) {
			return null
		}

		// Collect values from BOTH form inputs and report averages
		// This handles mixed scenarios like manager_evaluation where:
		// - Some components have write access (manager's input)
		// - Other components show report averages (aggregated rater scores)
		const allValues: number[] = []

		if (allFormComponents) {
			for (let index = 0; index < selectedComponentIds.length; index++) {
				const componentId = selectedComponentIds[index]
				const foundComponent = findComponentById(componentId, allFormComponents)

				if (!foundComponent) continue

				// First, try to get report averages (for read-only report components)
				// Weight the average by the number of submissions it represents
				if (foundComponent.reports && foundComponent.reports.length > 0) {
					for (const report of foundComponent.reports) {
						if (report.submissions) {
							// When excludeParticipants is true, we need to recalculate from individual submissions
							// instead of using the pre-calculated average
							for (const submission of report.submissions) {
								// Skip placeholder submissions (they have empty values and shouldn't be included in averages)
								if (submission.isPlaceholder) {
									continue
								}

								// Skip participant submissions if excludeParticipants is enabled
								if (excludeParticipants && submission.calculatedRole === 'participant') {
									continue
								}

								// Skip non-participant submissions if selfRatingsOnly is enabled
								if (selfRatingsOnly && submission.calculatedRole !== 'participant') {
									continue
								}

								const numValue = Number.parseFloat(submission.value)
								if (!Number.isNaN(numValue) && Number.isFinite(numValue)) {
									allValues.push(numValue)
								}
							}
						}
					}
				}
				// If no reports, try to get the live form value (for editable input components)
				else {
					// When excludeParticipants is true, a participant's own live input must not
					// contribute to the average — mirroring the reports-branch skip above.
					// Fall back to 'participant' when userRole is empty (self-eval context).
					if (excludeParticipants && (userRole || 'participant') === 'participant') continue
					if (selfRatingsOnly && (userRole || 'participant') !== 'participant') continue

					const watchedValue = watchedValues?.[index]

					if (watchedValue !== undefined && watchedValue !== null && watchedValue !== '') {
						const numValue = typeof watchedValue === 'number' ? watchedValue : Number.parseFloat(String(watchedValue))
						if (!Number.isNaN(numValue) && Number.isFinite(numValue)) {
							allValues.push(numValue)
						}
					}
				}
			}
		}

		// If we have any values, calculate the average
		if (allValues.length > 0) {
			const sum = allValues.reduce((acc, val) => acc + val, 0)
			return sum / allValues.length
		}

		return null
	}, [watchedValues, selectedComponentIds, allFormComponents, excludeParticipants, userRole, selfRatingsOnly])

	return <FormsAverage component={component} value={averageValue} />
}

const DataViewerComponent = ({
	component,
	evaluationId,
}: {
	component: FormComponentWithReports
	evaluationId: string
}) => {
	const {
		dataSources,
		excludeSelfResponses,
		excludeRaterResponses,
		excludePrimaryManagerResponses,
		excludeSkipManagerResponses,
	} = component.custom as DataViewerCustomProps

	const { isEnabled: isDataViewerRichTextEnabled } = useFeatureFlag('data-viewer-rich-text')

	const { data, isLoading } = useQuery({
		...trpc.client.viewers.getDataViewerResponses.queryOptions({
			evaluationId,
			dataSources: dataSources ?? [],
			viewerComponentId: component.id,
			excludeSelfResponses: !!excludeSelfResponses,
			excludeRaterResponses: !!excludeRaterResponses,
			excludePrimaryManagerResponses: !!excludePrimaryManagerResponses,
			excludeSkipManagerResponses: !!excludeSkipManagerResponses,
		}),
		enabled: dataSources?.length > 0,
	})

	return (
		<FormsDataViewer component={component} data={data} loading={isLoading} renderAsHtml={isDataViewerRichTextEnabled} />
	)
}

const AggregationComponent = ({
	component,
	evaluationId,
	allFormComponents,
}: {
	component: FormComponentWithReports
	evaluationId: string
	allFormComponents?: FormComponentWithReports[]
}) => {
	const { selectedComponentIds, aggregationType, grouping } = component.custom as {
		selectedComponentIds?: string[]
		aggregationType?:
			| 'max'
			| 'minimum'
			| 'average'
			| 'distribution'
			| 'mostVoted'
			| 'leastVoted'
			| 'groupByRole'
			| 'combine'
		grouping?: 'combine' | 'groupByRole' | 'groupByRoleAndRateGroup'
	}
	const componentIds = selectedComponentIds ?? []
	const aggType = aggregationType || 'groupByRole'

	const sourceComponentName = componentIds.reduce<string | null>((found, id) => {
		if (found || !id) return found
		return findComponentById(id, allFormComponents)?.name ?? null
	}, null)

	const { data, isLoading } = useQuery({
		...trpc.client.viewers.getAggregationResponses.queryOptions({
			evaluationId,
			componentIds,
			aggregationType: aggType,
			grouping,
		}),
		enabled: componentIds.length > 0 && !!aggregationType,
	})

	return (
		<FormsAggregation
			component={component}
			data={data?.kind === 'text' ? data.data : undefined}
			optionData={data?.kind === 'option' ? data.optionData : undefined}
			numericValue={data?.kind === 'numeric' ? data.numericValue : undefined}
			roles={data?.kind === 'byRole' ? data.roles : undefined}
			loading={isLoading}
			renderAsHtml={sourceComponentName === 'textarea'}
		/>
	)
}

export const ComponentRenderer = ({
	component,
	control,
	evaluationId,
	currentStepId,
	userRole,
	canEdit,
	allowCAEdit,
	taskId,
	refetch,
	fieldErrors,
	allFormComponents,
	aiSummaries,
	onDictationSnapshot,
	onSubmitDraft,
	isHidden,
	evaluateeExternalId,
}: {
	component: FormComponentWithReports
	control: Control<FormValues>
	evaluationId: string
	currentStepId: string
	userRole: string
	canEdit: boolean
	allowCAEdit?: boolean
	taskId?: string
	refetch?: () => void
	fieldErrors?: Set<string>
	allFormComponents?: FormComponentWithReports[]
	aiSummaries?: AiSummary[]
	onDictationSnapshot?: (snapshot: DictationReviewSnapshot) => void
	onSubmitDraft?: () => void
	isHidden?: (componentId: string) => boolean
	evaluateeExternalId?: string
}) => {
	const toast = useToast()
	const isReviewViewer = component.name === 'reviewviewer'
	const reviewViewerCustomProps = isReviewViewer ? (component.custom as FormsReviewViewerCustomProps) : undefined
	const reviewSegment = reviewViewerCustomProps?.reviewSegment
	const isDynamicMode = isReviewViewer && !reviewSegment
	const { data: reviewViewerSignedUrl } = useReviewViewerUrl(evaluationId, isReviewViewer ? reviewSegment : undefined)
	const { data: reviewViewerArchiveFiles } = useReviewViewerUrls(evaluationId, isDynamicMode)

	const fieldName = component.id
	// Field is read-only if either: step doesn't allow editing OR user doesn't have write permission
	const readOnly = !canEdit || !component.write
	const required = !component.optional
	const hasError = fieldErrors?.has(component.id) || false
	const { isEnabled: isAiFeedbackImprovementEnabled } = useFeatureFlag('ai-feedback-improvement')
	const { improve, isPending: isImprovingFeedback, reset: resetImproveFeedback } = useImproveFeedback()
	const [aiSuggestions, setAiSuggestions] = useState<FeedbackSuggestion[]>([])
	const { isEnabled: dictationEnabled } = useFeatureFlag('voice-dictation')

	const requestTokenMutation = useMutation({
		...trpc.client.tasks.requestDictationSessionToken.mutationOptions(),
	})
	const rewriteMutation = useMutation({
		...trpc.client.tasks.rewriteDictatedText.mutationOptions(),
	})

	if (isHidden?.(component.id)) return null

	switch (component.name) {
		case 'text': {
			const textComponentWithReports = component as FormComponentWithReports
			const textHasReports = textComponentWithReports.reports && Array.isArray(textComponentWithReports.reports)

			const shouldShowInput = !textComponentWithReports.reportOnly && (component.write || textHasReports)

			const textHasNonEmptyReports =
				textHasReports && textComponentWithReports.reports?.some((r) => r.submissions.length > 0)
			const shouldShowTextEmptyState = textComponentWithReports.reportOnly && !textHasNonEmptyReports

			return (
				<>
					{textHasNonEmptyReports ? (
						<TextFieldReports component={component} reports={textComponentWithReports.reports ?? []} />
					) : null}
					{shouldShowInput ? (
						<Controller
							control={control}
							name={fieldName}
							defaultValue={getInitialFormValue(component, 'text')}
							render={({ field }) => (
								<div className={readOnly ? 'pointer-events-none' : ''}>
									<FormsTextField
										component={component}
										value={field.value as string}
										onChange={(e: React.ChangeEvent<HTMLInputElement>) => field.onChange(e.target.value)}
										readOnly={readOnly}
										required={required}
										hasError={hasError}
										dictationEnabled={dictationEnabled && !readOnly}
										allowAiRewrite={dictationEnabled}
										onRequestDictationToken={async () => await requestTokenMutation.mutateAsync({ scope: 'dictation' })}
										onRewriteDictatedText={async (input: string, tone: DictationTone) =>
											(
												await rewriteMutation.mutateAsync({
													text: input,
													tone,
												})
											).rewrittenText
										}
										onDictationSnapshot={(snapshot) => onDictationSnapshot?.(snapshot)}
									/>
								</div>
							)}
						/>
					) : null}
					{shouldShowTextEmptyState ? (
						<div className='opacity-60 pointer-events-none'>
							<FormsTextField component={component} value='' onChange={() => {}} readOnly={false} disabled={true} />
						</div>
					) : null}
				</>
			)
		}
		case 'rating': {
			const componentWithReports = component as FormComponentWithReports
			const hasReports = componentWithReports.reports && Array.isArray(componentWithReports.reports)

			const shouldShowRatingInput = !componentWithReports.reportOnly && (component.write || hasReports)

			const ratingHasNonEmptyReports = hasReports && componentWithReports.reports?.some((r) => r.submissions.length > 0)
			const shouldShowRatingEmptyState = componentWithReports.reportOnly && !ratingHasNonEmptyReports

			return (
				<>
					{shouldShowRatingInput ? (
						<Controller
							control={control}
							name={fieldName}
							defaultValue={getInitialFormValue(component, 'rating')}
							render={({ field }) => {
								return (
									<div className='flex'>
										<div
											className={cn(
												componentWithReports.reportOnly &&
													hasReports &&
													'bg-neutral-50 border border-neutral-200 rounded-md',
											)}>
											<div className={readOnly ? 'pointer-events-none' : ''}>
												<FormsRating
													component={component}
													value={field.value as string}
													onChange={(value: string) => field.onChange(value)}
													readOnly={readOnly}
													required={required}
													hasError={hasError}
												/>
											</div>
										</div>
									</div>
								)
							}}
						/>
					) : null}
					{ratingHasNonEmptyReports ? (
						<RatingReports
							component={component}
							reports={componentWithReports.reports ?? []}
							allowCAEdit={allowCAEdit}
							taskId={taskId}
							refetch={refetch}
						/>
					) : null}
					{shouldShowRatingEmptyState ? (
						<div className='opacity-60 pointer-events-none'>
							<FormsRating component={component} value='' onChange={() => {}} readOnly={true} />
						</div>
					) : null}
				</>
			)
		}
		case 'checkbox': {
			const checkboxComponentWithReports = component as FormComponentWithReports
			const checkboxHasReports =
				checkboxComponentWithReports.reports && Array.isArray(checkboxComponentWithReports.reports)

			const shouldShowCheckboxInput =
				!checkboxComponentWithReports.reportOnly && (component.write || checkboxHasReports)

			const checkboxHasNonEmptyReports =
				checkboxHasReports && checkboxComponentWithReports.reports?.some((r) => r.submissions.length > 0)
			const shouldShowCheckboxEmptyState = checkboxComponentWithReports.reportOnly && !checkboxHasNonEmptyReports

			return (
				<>
					{checkboxHasNonEmptyReports ? (
						<CheckboxReports component={component} reports={checkboxComponentWithReports.reports ?? []} />
					) : null}
					{shouldShowCheckboxInput ? (
						<Controller
							control={control}
							name={fieldName}
							defaultValue={getInitialBoolean(component, false, 'defaultChecked')}
							render={({ field }) => (
								<FormsCheckbox
									component={component}
									checked={field.value as boolean}
									onChange={(e: React.ChangeEvent<HTMLInputElement>) => field.onChange(e.target.checked)}
									readOnly={readOnly}
									hasError={hasError}
									required={required}
								/>
							)}
						/>
					) : null}
					{shouldShowCheckboxEmptyState ? (
						<div className='opacity-60 pointer-events-none'>
							<FormsCheckbox component={component} checked={false} onChange={() => {}} readOnly={true} />
						</div>
					) : null}
				</>
			)
		}
		case 'number': {
			const numberComponentWithReports = component as FormComponentWithReports
			const numberHasReports = numberComponentWithReports.reports && Array.isArray(numberComponentWithReports.reports)

			const shouldShowNumberInput = !numberComponentWithReports.reportOnly && (component.write || numberHasReports)

			const numberHasNonEmptyReports =
				numberHasReports && numberComponentWithReports.reports?.some((r) => r.submissions.length > 0)
			const shouldShowNumberEmptyState = numberComponentWithReports.reportOnly && !numberHasNonEmptyReports

			return (
				<>
					{numberHasNonEmptyReports ? (
						<NumberFieldReports component={component} reports={numberComponentWithReports.reports ?? []} />
					) : null}
					{shouldShowNumberInput ? (
						<Controller
							control={control}
							name={fieldName}
							defaultValue={getInitialFormValue(component, 'number')}
							render={({ field }) => (
								<FormsNumberField
									component={component}
									value={String(field.value)}
									onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
										const val = e.target.value
										if (val === '') {
											field.onChange('')
											return
										}
										const numVal = Number.parseFloat(val)
										if (!Number.isNaN(numVal)) {
											field.onChange(numVal)
										}
									}}
									readOnly={readOnly}
									required={required}
									hasError={hasError}
								/>
							)}
						/>
					) : null}
					{shouldShowNumberEmptyState ? (
						<div className='opacity-60 pointer-events-none'>
							<FormsNumberField component={component} value='' onChange={() => {}} readOnly={true} />
						</div>
					) : null}
				</>
			)
		}
		case 'textarea': {
			const textAreaComponentWithReports = component as FormComponentWithReports
			const textAreaHasReports =
				textAreaComponentWithReports.reports && Array.isArray(textAreaComponentWithReports.reports)
			const shouldShowTextAreaInput =
				!textAreaComponentWithReports.reportOnly && (component.write || textAreaHasReports)

			const textAreaHasNonEmptyReports =
				textAreaHasReports && textAreaComponentWithReports.reports?.some((r) => r.submissions.length > 0)
			const shouldShowTextAreaEmptyState = textAreaComponentWithReports.reportOnly && !textAreaHasNonEmptyReports
			const shouldShowImproveAction = shouldShowTextAreaInput && !readOnly && isAiFeedbackImprovementEnabled

			return (
				<>
					{textAreaHasNonEmptyReports ? (
						<TextAreaReports
							component={component}
							reports={textAreaComponentWithReports.reports ?? []}
							allowCAEdit={allowCAEdit}
							taskId={taskId}
							refetch={refetch}
						/>
					) : null}
					{shouldShowTextAreaInput ? (
						<Controller
							control={control}
							name={fieldName}
							defaultValue={getInitialFormValue(component, 'text')}
							render={({ field }) => {
								const currentText = String(field.value || '').trim()
								const isTooShort = currentText.length < 20
								const isEmpty = currentText.length === 0

								return (
									<div className={readOnly ? 'pointer-events-none' : ''}>
										<FormsTextArea
											component={component}
											value={field.value as string}
											onChange={(value: string) => field.onChange(value)}
											readOnly={readOnly}
											required={required}
											hasError={hasError}
											dictationEnabled={dictationEnabled && !readOnly}
											allowAiRewrite={dictationEnabled}
											onRequestDictationToken={async () =>
												await requestTokenMutation.mutateAsync({ scope: 'dictation' })
											}
											onRewriteDictatedText={async (input: string, tone: DictationTone) =>
												(
													await rewriteMutation.mutateAsync({
														text: input,
														tone,
													})
												).rewrittenText
											}
											onDictationSnapshot={(snapshot) => onDictationSnapshot?.(snapshot)}
										/>
										{shouldShowImproveAction ? (
											<div className='mt-2 flex items-center gap-2'>
												<Tooltip>
													<TooltipTrigger asChild>
														<span>
															<Button
																variant='secondary'
																size='small'
																icon={IaIcon}
																disabled={isEmpty || isImprovingFeedback}
																onClick={async () => {
																	if (isTooShort) {
																		toast.showToast(
																			'Please write at least 20 characters before requesting suggestions.',
																			'info',
																		)
																		return
																	}
																	try {
																		const result = await improve({ feedbackText: currentText })
																		setAiSuggestions(result.suggestions)
																	} catch {
																		toast.showToast('AI feedback improvement is not available right now.', 'error')
																	}
																}}>
																Improve with AI
															</Button>
														</span>
													</TooltipTrigger>
													{isEmpty ? <TooltipContent>Write some feedback first</TooltipContent> : null}
												</Tooltip>
											</div>
										) : null}
										{shouldShowImproveAction && isImprovingFeedback ? <FeedbackSuggestionsLoading /> : null}
										{shouldShowImproveAction && !isImprovingFeedback && aiSuggestions.length > 0 ? (
											<FeedbackSuggestionsPanel
												suggestions={aiSuggestions}
												onApply={(suggestion) => {
													field.onChange(suggestion.proposedRewrite)
													setAiSuggestions((prev) => prev.filter((item) => item.id !== suggestion.id))
												}}
												onDismiss={(suggestion) => {
													setAiSuggestions((prev) => prev.filter((item) => item.id !== suggestion.id))
												}}
												onClose={() => {
													setAiSuggestions([])
													resetImproveFeedback()
												}}
											/>
										) : null}
									</div>
								)
							}}
						/>
					) : null}
					{shouldShowTextAreaEmptyState ? (
						<div className='opacity-60 pointer-events-none'>
							<FormsTextArea component={component} value='' onChange={() => {}} readOnly={true} />
						</div>
					) : null}
				</>
			)
		}
		case 'select': {
			const selectComponentWithReports = component as FormComponentWithReports
			const selectHasReports = selectComponentWithReports.reports && Array.isArray(selectComponentWithReports.reports)

			const shouldShowSelectInput = !selectComponentWithReports.reportOnly && (component.write || selectHasReports)

			const selectHasNonEmptyReports =
				selectHasReports && selectComponentWithReports.reports?.some((r) => r.submissions.length > 0)
			const shouldShowSelectEmptyState = selectComponentWithReports.reportOnly && !selectHasNonEmptyReports

			return (
				<>
					{selectHasNonEmptyReports ? (
						<SelectFieldReports component={component} reports={selectComponentWithReports.reports ?? []} />
					) : null}
					{shouldShowSelectInput ? (
						<Controller
							control={control}
							name={fieldName}
							defaultValue={getInitialFormValue(component, 'text')}
							render={({ field }) => (
								<FormsSelectField
									component={component}
									value={field.value as string}
									onChange={(value: string) => field.onChange(value)}
									readOnly={readOnly}
									required={required}
									hasError={hasError}
								/>
							)}
						/>
					) : null}
					{shouldShowSelectEmptyState ? (
						<div className='opacity-60 pointer-events-none'>
							<FormsSelectField component={component} value='' onChange={() => {}} readOnly={true} />
						</div>
					) : null}
				</>
			)
		}
		case 'date': {
			const dateComponentWithReports = component as FormComponentWithReports
			const dateHasReports = dateComponentWithReports.reports && Array.isArray(dateComponentWithReports.reports)

			const shouldShowDateInput = !dateComponentWithReports.reportOnly && (component.write || dateHasReports)

			const dateHasNonEmptyReports =
				dateHasReports && dateComponentWithReports.reports?.some((r) => r.submissions.length > 0)
			const shouldShowDateEmptyState = dateComponentWithReports.reportOnly && !dateHasNonEmptyReports

			return (
				<>
					{dateHasNonEmptyReports ? (
						<DatePickerReports component={component} reports={dateComponentWithReports.reports ?? []} />
					) : null}
					{shouldShowDateInput ? (
						<Controller
							control={control}
							name={fieldName}
							defaultValue={getInitialFormValue(component, 'text')}
							render={({ field }) => (
								<div className={readOnly ? 'pointer-events-none' : ''}>
									<FormsDatePicker
										component={component}
										value={field.value as string}
										onChange={(value: string) => field.onChange(value)}
										readOnly={readOnly}
										required={required}
										hasError={hasError}
									/>
								</div>
							)}
						/>
					) : null}
					{shouldShowDateEmptyState ? (
						<div className='opacity-60 pointer-events-none'>
							<FormsDatePicker component={component} value='' onChange={() => {}} readOnly={true} />
						</div>
					) : null}
				</>
			)
		}
		case 'repeatable':
			return (
				<RepeatableComponent
					component={component}
					control={control}
					evaluationId={evaluationId}
					currentStepId={currentStepId}
					userRole={userRole}
					canEdit={canEdit}
					allowCAEdit={allowCAEdit}
					taskId={taskId}
					refetch={refetch}
					fieldErrors={fieldErrors}
					allFormComponents={allFormComponents}
					aiSummaries={aiSummaries}
					onDictationSnapshot={onDictationSnapshot}
					onSubmitDraft={onSubmitDraft}
					isHidden={isHidden}
					evaluateeExternalId={evaluateeExternalId}
				/>
			)
		case 'container':
			return (
				<ContainerComponent
					component={component}
					control={control}
					evaluationId={evaluationId}
					currentStepId={currentStepId}
					userRole={userRole}
					canEdit={canEdit}
					allowCAEdit={allowCAEdit}
					taskId={taskId}
					refetch={refetch}
					fieldErrors={fieldErrors}
					allFormComponents={allFormComponents}
					aiSummaries={aiSummaries}
					onDictationSnapshot={onDictationSnapshot}
					onSubmitDraft={onSubmitDraft}
					isHidden={isHidden}
					evaluateeExternalId={evaluateeExternalId}
				/>
			)
		case 'heading':
			return <FormsHeader component={component} />
		case 'instructions':
			return <FormsInstructions component={component} />
		case 'paragraph':
			return <FormsParagraph component={component} />
		case 'divider':
			return <FormsDivider component={component} />
		case 'reviewviewer': {
			const customLinkText =
				((component.custom as Record<string, unknown>)?.linkText as string) ||
				'View 2024 Year-End Performance Evaluation'
			const reviewViewerComponent = {
				...component,
				custom: {
					...(component.custom as Record<string, unknown>),
					linkText: customLinkText,
				},
			}
			return (
				<FormsReviewViewer
					component={reviewViewerComponent}
					signedUrl={isDynamicMode ? undefined : reviewViewerSignedUrl}
					archiveFiles={isDynamicMode ? (reviewViewerArchiveFiles ?? undefined) : undefined}
				/>
			)
		}
		case 'externallink':
			return <FormsExternalLink component={component} />
		case '9boxrating':
			return (
				<Controller
					control={control}
					name={fieldName}
					// Seed from the saved response (a string in the form-response store) so a
					// previously-selected box is restored on reload. Forms9BoxRating coerces
					// string ↔ number internally for the cell-highlight comparison.
					defaultValue={getInitialFormValue(component, 'rating')}
					render={({ field }) => (
						<Forms9BoxRating
							component={component}
							value={field.value as number | string | undefined}
							onChange={(value: number) => field.onChange(value)}
							required={required}
							hasError={hasError}
						/>
					)}
				/>
			)
		case 'average':
			return (
				<AverageComponent
					component={component}
					control={control}
					allFormComponents={allFormComponents}
					userRole={userRole}
				/>
			)
		case 'radio-group': {
			const radioComponentWithReports = component as FormComponentWithReports
			const radioHasReports = radioComponentWithReports.reports && Array.isArray(radioComponentWithReports.reports)
			const radioHasNonEmptyReports =
				radioHasReports && radioComponentWithReports.reports?.some((r) => r.submissions.length > 0)
			const shouldShowRadioInput = !radioComponentWithReports.reportOnly && (component.write || radioHasReports)
			const shouldShowRadioEmptyState = radioComponentWithReports.reportOnly && !radioHasNonEmptyReports

			return (
				<>
					{shouldShowRadioInput ? (
						<Controller
							control={control}
							name={fieldName}
							defaultValue={getInitialFormValue(component, 'text', 'defaultChoice')}
							render={({ field }) => (
								<ChoiceSelectField
									component={component}
									value={field.value as string}
									onChange={(value: string) => field.onChange(value)}
									readOnly={readOnly}
									required={required}
									hasError={hasError}
								/>
							)}
						/>
					) : null}
					{radioHasNonEmptyReports ? (
						<RadioGroupReports component={component} reports={radioComponentWithReports.reports ?? []} />
					) : null}
					{shouldShowRadioEmptyState ? (
						<div className='opacity-60 pointer-events-none'>
							<ChoiceSelectField component={component} value='' onChange={() => {}} readOnly={true} />
						</div>
					) : null}
				</>
			)
		}
		case 'image':
			return <FormsImage component={component} enableLightbox={true} />
		case 'ai_summary':
			// AI summaries are rendered separately via AiSummaryList in CycleExecution
			return null
		case 'dataviewer':
			return <DataViewerComponent component={component} evaluationId={evaluationId} />
		case 'aggregation':
			return (
				<AggregationComponent component={component} evaluationId={evaluationId} allFormComponents={allFormComponents} />
			)
		case 'external_data_view':
			return <FormsExternalDataView component={component} evaluateeExternalId={evaluateeExternalId} />
		case 'hrreviewflag':
			return <HrReviewFlagComponent component={component} evaluationId={evaluationId} readOnly={readOnly} />
		default:
			return <Text>{component.name}</Text>
	}
}
